import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { idempotencyHeaders, platformErrorMessage, platformGet, platformPost } from '@/apps/ingestion/services/_platformClient';

const SUPPORT_BASE = '/platform/v1/support';

async function supportFetch<T>(path: string): Promise<T> {
  const { data, error, response } = await platformGet(path);
  if (error) {
    const status = response?.status;
    throw new Error(status === 404 ? 'Not found' : platformErrorMessage(error, `Request failed: ${status ?? 'unknown'}`));
  }
  return data as T;
}

export const supportKeys = {
  all: ['support'] as const,
  stats: () => [...supportKeys.all, 'stats'] as const,
  issues: (filters: Record<string, string>) => [...supportKeys.all, 'issues', filters] as const,
  issue: (issueNumber: number | string) => [...supportKeys.all, 'issue', String(issueNumber)] as const,
  comments: (issueNumber: number | string) => [...supportKeys.all, 'comments', String(issueNumber)] as const,
};

export interface GitHubIssue {
  number: number;
  title: string;
  body: string;
  state: string;
  html_url: string;
  user: string | null;
  labels: string[];
  issue_type: string | null;
  created_at: string;
  updated_at: string;
  due_on: string | null;
  project_fields: Record<string, string | number | null> | null;
}

export interface GitHubIssueDetail extends GitHubIssue {
  assignees: string[];
  milestone: string | null;
  closed_at: string | null;
}

export interface IssueComment {
  id: number;
  body: string;
  user: string | null;
  created_at: string;
  html_url: string | null;
}

export interface IssueStats {
  open: number;
  closed: number;
  open_bugs: number;
  closed_bugs: number;
}

interface IssuesPage {
  object: 'list';
  data: GitHubIssue[];
  has_more: boolean;
  next_cursor: string | null;
  stats: IssueStats;
  request_id: string;
}

interface CommentsPage {
  object: 'list';
  data: IssueComment[];
  has_more: boolean;
  next_cursor: string | null;
  request_id: string;
}

export function useSupportIssues(filters: { state: string; issueType: string; sort: string; perPage: number; cursor: string | null }) {
  const params = new URLSearchParams({
    state: filters.state,
    sort: filters.sort,
    limit: String(filters.perPage),
  });
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.issueType !== 'all') params.set('issue_type', filters.issueType);

  return useQuery({
    queryKey: supportKeys.issues({
      state: filters.state,
      issueType: filters.issueType,
      sort: filters.sort,
      cursor: filters.cursor ?? '',
      perPage: String(filters.perPage),
    }),
    queryFn: async () => {
      const page = await supportFetch<IssuesPage>(`${SUPPORT_BASE}/issues?${params}`);
      return page;
    },
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });
}

export function useSupportIssue(issueNumber: string | undefined) {
  return useQuery({
    queryKey: supportKeys.issue(issueNumber ?? ''),
    queryFn: () => supportFetch<GitHubIssueDetail>(`${SUPPORT_BASE}/issues/${issueNumber}`),
    enabled: !!issueNumber,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

export function useSupportComments(issueNumber: number | string | undefined, options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: supportKeys.comments(issueNumber ?? ''),
    queryFn: async () => {
      const page = await supportFetch<CommentsPage>(`${SUPPORT_BASE}/issues/${issueNumber}/comments`);
      return page.data;
    },
    enabled: !!issueNumber,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    refetchInterval: options?.refetchInterval,
    retry: 1,
  });
}

/** Post a plain comment ("add more info") to a support issue and refresh its comment list. */
export function useCreateSupportComment(issueNumber: number | string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const { data, error, response } = await platformPost(`${SUPPORT_BASE}/issues/${issueNumber}/comments`, {
        body: { body },
        headers: idempotencyHeaders(),
      });
      if (error) {
        throw new Error(platformErrorMessage(error, `Request failed: ${response?.status ?? 'unknown'}`));
      }
      return data as IssueComment;
    },
    onSuccess: () => {
      if (issueNumber != null) {
        void queryClient.invalidateQueries({ queryKey: supportKeys.comments(issueNumber) });
      }
    },
  });
}

export function useInvalidateSupport() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: supportKeys.all });
  }, [queryClient]);
}
